import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Modal } from "antd";
import useAuthGuard from "../hooks/useAuthGuard";
import { isProdHost } from "../Utility/isProdHost";
import TabSessionGuard from "./CommonFunctionality/TabSessionGuard";
import SubscriptionGuard from "./Subscription/flag/SubscriptionGuard";
import { useSubscriptionFlag } from "./Subscription/flag/subscriptionFlagStore";
import { isFreeUser } from "./Subscription/flag/flagReasons";
import "./AutopayMandate/MandateAutopayStep.css";

const AUTOPAY_MANDATE_PATH = "/autopay-mandate";

const ProtectedRoute = () => {
  useAuthGuard();
  const location = useLocation();

  // AutoPay mandate gate — Login.jsx sets "autopayMandateActive" to
  // "false" right after login when the DSA has no active UPI AutoPay
  // mandate yet (useMandateAutopay flips it to "true" once one succeeds).
  // While it's "false", every protected screen is blocked in favour of
  // /autopay-mandate so the DSA can't navigate away before completing it.
  // Live-only (rm.kanchaneshver.com) — devrm.kanchaneshver.com and any
  // other non-live host never blocks, matching Login.jsx which never sets
  // "autopayMandateActive" there in the first place.
  //
  // ⚠️ This must redirect via <Navigate> during render, NOT via
  // navigate() inside a useEffect after rendering <Outlet />. With
  // <Outlet />, the target screen mounts and fires its own API calls
  // *before* a post-mount effect could redirect away — every blocked nav
  // click was quietly triggering that other screen's requests. Returning
  // <Navigate> here instead means the target screen's route element never
  // mounts at all.
  // Free User Access employees (flag EXEMPT) have no AutoPay at all, so
  // they are never sent to the mandate screen.
  const { flag: subscriptionFlag } = useSubscriptionFlag();
  const mandateActive = localStorage.getItem("autopayMandateActive");
  const mandateBlocked =
    isProdHost() &&
    mandateActive === "false" &&
    !isFreeUser(subscriptionFlag) &&
    location.pathname !== AUTOPAY_MANDATE_PATH;

  useEffect(() => {
    if (mandateBlocked) {
      // A styled modal (vs. a toast) so this is impossible to miss/ignore
      // when the DSA tries clicking into another screen.
      Modal.warning({
        icon: null,
        title: null,
        className: "mandate-block-modal",
        centered: true,
        closable: false,
        maskClosable: false,
        okText: "Go to AutoPay Setup",
        content: (
          <div className="mandate-block">
            <div className="mandate-block__icon">
              <i className="bx bx-shield-quarter" />
            </div>
            <h5 className="mandate-block__title">
              Complete AutoPay Setup First
            </h5>
            <p className="mandate-block__text">
              You need to successfully set up AutoPay before you can access
              any other screen.
            </p>
          </div>
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, mandateBlocked]);

  if (mandateBlocked) {
    return (
      <Navigate to={AUTOPAY_MANDATE_PATH} replace state={{ forceSetup: true }} />
    );
  }

  return (
    <>
      <TabSessionGuard />
      <SubscriptionGuard />
      <Outlet />
    </>
  );
};

export default ProtectedRoute;
